import { PaymentPlanMetadata } from "~graphql/sdk";
import { Icon, Modal } from "~components/common";
import {
  Box,
  Price,
  Stack,
  Text,
  LegacyText,
  UnderlineButton,
} from "flicket-ui";
import { useOrganization } from "~hooks";

interface CheckoutPaymentPlansModalProps {
  metadata: PaymentPlanMetadata;
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  membershipId: string;
}

export const PaymentPlansModal = ({
  metadata,
  isOpen,
  onClose,
  eventId,
  membershipId,
}: CheckoutPaymentPlansModalProps) => {
  const { t, organization } = useOrganization();

  const dayOfMonth = new Date().getDate();
  const suffix =
    dayOfMonth === 1 || dayOfMonth === 21 || dayOfMonth === 31
      ? "st"
      : dayOfMonth === 2 || dayOfMonth === 22
      ? "nd"
      : dayOfMonth === 3 || dayOfMonth === 23
      ? "rd"
      : "th";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      height="auto"
      bg="transparent"
      aria-label="payment-plans-dialog"
      maxW={"620px"}
      p={2}
    >
      <Box position={"relative"} background={"white"} rounded={"sm"}>
        <Icon
          icon="close"
          position={"absolute"}
          right={"2"}
          top={"2"}
          fontSize={6}
          float="right"
          onClick={() => onClose()}
        />
        <Text variant="header.S" py={2} px={4}>
          Payment plan details
        </Text>
        <Stack
          direction={"vertical"}
          gap={2}
          py={4}
          px={4}
          borderTop={"1"}
          borderTopColor={"N200"}
          borderTopStyle={"solid"}
        >
          <Stack direction={"vertical"} gap={0}>
            <Text variant="header.XS">
              Initial deposit &bull;{" "}
              <Price
                fontWeight={"regular"}
                price={metadata.depositAmount}
                currency={organization.currency}
              />
            </Text>
            <Text variant="regular">
              Reserve your spot by paying a{" "}
              {(100 / metadata.installmentCount).toFixed(0)}% deposit today,
              plus fees and ticket protection cover.
            </Text>
          </Stack>
          <Stack direction={"vertical"} gap={0}>
            <Text variant="header.XS">
              Monthly payments{" "}
              <LegacyText as="span" fontWeight={"regular"}>
                &bull;
                <Price
                  fontWeight={"regular"}
                  price={metadata.installmentAmount}
                  currency={organization.currency}
                />{" "}
                x {metadata.installmentCount - 1}
              </LegacyText>
            </Text>
            <Text variant="regular">
              Your credit card will be charged automatically on the {dayOfMonth}
              {suffix} of each month.
            </Text>
          </Stack>
          <Text variant="small">
            Your{" "}
            {eventId ? "tickets" : t("membership_name_plural").toLowerCase()}{" "}
            will be issued to you after your account is paid in full. Failure to
            complete payment on schedule may result in order cancellation and
            loss of payments.
          </Text>
          <Text variant="regular">
            <UnderlineButton
              href={
                eventId
                  ? `/events/${eventId}/terms-and-conditions`
                  : `/memberships/${membershipId}/terms-and-conditions`
              }
              lineHeight="normal"
              fontWeight="regular"
              color="N600"
              display="inline"
              verticalAlign={"baseline"}
              target="_blank"
            >
              Terms and conditions
            </UnderlineButton>
          </Text>
        </Stack>
      </Box>
    </Modal>
  );
};
